/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from 'react';
import commonSelector, {
  selectedCustomerIdSelector,
  bleEnabledSelector,
  supportedMetricsSelector,
} from '@store/common/selectors';
import { Dropdown, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { selector as defaultLandingPageSelector } from '@store/defaultLandingPage';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import featureNames from '@utils/featureToggle/constants';
import { SharingAdapter } from '@components/common/Sharing';
import { SaveDashboardAdapter } from '@components/common/Dashboard/FunctionalButton';
import { DASHBOARD_TYPE, PAGE_TYPE } from '@utils/constants';
import { EXPORT_VM_DATA } from '@store/export/types';
import * as dashboardsUIActions from '@store/dashboards/ui/actions';
import RemoveDashboardPopup from '@components/common/Dashboard/RemoveDashboardPopup';
import {
  onSetDefaultLandingApply,
  onSetDefaultLanding,
  dlpTitle,
  dlpNeedInput,
  dlpContent,
} from '@components/common/kebabMenuUtils/defaultLandingPage';
import { useViewerStore } from '@store/viewerModule/base';
import { setKebabMenuOptions } from './kebab-options';
import { MarginLayout } from './styled';
import { getSharingState, getExportConfig, checkAdvancedMetricsEnabled } from '../common/utils';
import { viewerMetricList } from '../common/mappings';
import { MoreOutlined } from '@ant-design/icons';

const { baseUrl } = window.conviva;
// eslint-disable-next-line react/display-name
export default ({ id, pageType }) => {
  const dispatch = useDispatch();
  const [{ viewerId, filterId, interval, selectedColumns }] = useViewerStore();
  const { isLive } = interval;
  const { features, profiles, interval: commonInterval } = useSelector(commonSelector);
  const selectedCustomerId = useSelector(selectedCustomerIdSelector);
  const sharingState = React.useMemo(
    () =>
      getSharingState(
        viewerId,
        interval,
        commonInterval,
        selectedCustomerId,
        selectedColumns.filter((it) => it.show).map((it) => it.metric),
      ),
    [viewerId, interval, commonInterval, selectedCustomerId, selectedColumns],
  );

  // Summary metrics and payload
  const isBusinessMetricEnabled = useSelector(bleEnabledSelector);
  const supportedMetrics = useSelector(supportedMetricsSelector);
  const isAdvancedMetricEnabled = checkAdvancedMetricsEnabled(supportedMetrics);
  const metricsList = viewerMetricList(isBusinessMetricEnabled, isAdvancedMetricEnabled);
  const [dispatchExport, setExportdispatch] = useState(false);

  // default landing page
  const defaultLandingPage = useSelector(defaultLandingPageSelector);
  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);
  const cid = profiles.filter((p) => p.is_selected)[0].customer_id;
  const dlpOkFn = () => onSetDefaultLandingApply(dispatch, cid);
  const setLandingPageEnabled =
    (features[featureNames.DEFAULT_LANDING_PAGE] || true) && pageType !== PAGE_TYPE.SHARING;

  const isVMShareEnabled = true;
  const isVMSaveEnabled = true;
  const [isShareClicked, setShareClicked] = useState(false);
  const [isSaveClicked, setSaveClicked] = useState(false);

  useEffect(() => {
    const updatedConfig = getExportConfig(
      viewerId,
      filterId,
      interval,
      metricsList,
      selectedColumns,
      isLive,
    );
    // Moved export dispatch from OnApply to useEffect
    // Updated state was not picked from the onApply method, hence all queries used state interval
    if (dispatchExport) {
      setExportdispatch(false);
      dispatch({
        type: EXPORT_VM_DATA,
        config: updatedConfig,
      });
    }
  }, [interval, viewerId, dispatchExport]);

  const setShareButtonState = (flag) => {
    setShareClicked(flag);
  };

  const kebabItems = React.useMemo(() => {
    const kebabs = setKebabMenuOptions(
      isDefaultLandingPage,
      setLandingPageEnabled,
      isVMShareEnabled,
      isVMSaveEnabled,
      pageType,
    );

    if (!isLive) {
      kebabs.push({
        key: 'export',
        label: 'Export',
      });
    }

    return kebabs;
  }, [setLandingPageEnabled, isDefaultLandingPage, isLive]);

  const onApply = ({ key: kebabId }: any) => {
    switch (kebabId) {
      case 'set-default-landing-page': {
        onSetDefaultLanding(dispatch, defaultLandingPage, cid);
        break;
      }
      case 'share': {
        setShareClicked(true);
        break;
      }
      case 'save': {
        dispatch(dashboardsUIActions.toggleUpdatePopupVisible(true, id));
        setSaveClicked(!isSaveClicked);
        break;
      }
      case 'delete': {
        dispatch(dashboardsUIActions.toggleRemovePopupVisible());
        break;
      }
      case 'export': {
        setExportdispatch(true);
        break;
      }
      default:
    }
  };

  return (
    <React.Fragment>
      <MarginLayout>
        <Dropdown menu={{ items: kebabItems, onClick: onApply }} trigger={['click']}>
          <Space style={{ lineHeight: 2, fontSize: 18 }}>
            <MoreOutlined />
          </Space>
        </Dropdown>
      </MarginLayout>

      {/* make default dialog modal */}
      <DefaultLandingPrompt
        title={dlpTitle}
        content={dlpContent(defaultLandingPage)}
        onOkFn={dlpOkFn}
        needInput={dlpNeedInput}
      />
      {
        // sharing modal
        isShareClicked && (
          <SharingAdapter
            isShareClicked={isShareClicked}
            config={sharingState}
            url={`${baseUrl}/dashboard/viewer-module/${viewerId}/sharing`}
            setShareClicked={setShareButtonState}
          />
        )
      }
      {
        // saving modal
        isSaveClicked && (
          <SaveDashboardAdapter
            pageType={pageType}
            id={id}
            dashboard={sharingState.state}
            dashboardType={DASHBOARD_TYPE.VIEWER_MODULE}
          />
        )
      }

      <RemoveDashboardPopup id={id} />
    </React.Fragment>
  );
};
